import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Snackbar,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Avatar
} from '@mui/material';
import {
  Support as SupportIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Close as CloseIcon,
  HelpOutline as HelpOutlineIcon,
  Article as ArticleIcon,
  LiveHelp as LiveHelpIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { createTicket, getTickets, sendLiveChatMessage, updateTicket } from '../firebase/services';
import { useLanguage, TranslationValue } from '../context/LanguageContext';
import LiveChat from '../components/LiveChat';

// Interface for tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Ticket interface
interface Ticket {
  id: string;
  ticketId: string; // ID unique du ticket
  subject: string;
  description: string;
  status: 'new' | 'inProgress' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  createdAt: any;
  updatedAt: any;
  userId: string;
  messages?: Message[];
}

// Message interface
interface Message {
  id: string;
  text: string;
  createdAt: any;
  userId: string;
  isStaff: boolean;
}

// Tab Panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Helper function to convert TranslationValue to string
const toStr = (value: TranslationValue): string => {
  return Array.isArray(value) ? value.join(' ') : value;
};

const SupportClient: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  
  // New ticket form state
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: '',
    priority: 'medium'
  });
  
  // Form errors
  const [errors, setErrors] = useState({
    subject: '',
    description: '',
    category: ''
  });

  // Ajouter un état pour le message de réponse
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);

  // Load user tickets
  useEffect(() => {
    const fetchTickets = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          const userTickets = await getTickets({ userId: currentUser.uid });
          setTickets(userTickets);
          setFilteredTickets(userTickets);
        } catch (error) {
          console.error("Error fetching tickets:", error);
          showNotification(toStr(t('supportclient.tickets.error')), 'error');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTickets();
  }, [currentUser, t]);

  // Ajoute un message d'accueil dans le tchat
  useEffect(() => {
    if (currentUser && !loading) {
      // On attend un peu pour que le composant LiveChat ait le temps de s'initialiser
      const timer = setTimeout(async () => {
        try {
          await sendLiveChatMessage(currentUser.uid, {
            text: `Bonjour ${currentUser.displayName || 'cher utilisateur'} ! Comment puis-je vous aider aujourd'hui ?`,
            userId: 'system',
            displayName: 'Support',
            isStaff: true,
            avatar: '/resources/support-avatar.png'
          });
        } catch (error) {
          console.error("Erreur lors de l'envoi du message d'accueil:", error);
        }
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [currentUser, loading]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    const status = event.target.value as string;
    setStatusFilter(status);
    if (status === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(ticket => ticket.status === status));
    }
  };

  // Nouveau handler pour TextField
  const handleNewTicketInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewTicket({
      ...newTicket,
      [name]: value
    });
  };

  // Nouveau handler pour Select
  const handleNewTicketSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewTicket({
      ...newTicket,
      [name]: value
    });
  };

  // Validate new ticket form
  const validateNewTicketForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    if (!newTicket.subject.trim()) {
      newErrors.subject = toStr(t('supportclient.newTicket.error'));
      valid = false;
    } else {
      newErrors.subject = '';
    }

    if (!newTicket.description.trim()) {
      newErrors.description = toStr(t('supportclient.newTicket.error'));
      valid = false;
    } else {
      newErrors.description = '';
    }

    if (!newTicket.category) {
      newErrors.category = toStr(t('supportclient.newTicket.error'));
      valid = false;
    } else {
      newErrors.category = '';
    }

    setErrors(newErrors);
    return valid;
  };

  // Handle new ticket submission
  const handleSubmitNewTicket = async () => {
    if (!validateNewTicketForm() || !currentUser) return;

    setLoading(true);
    try {
      await createTicket({
        subject: newTicket.subject,
        description: newTicket.description,
        category: newTicket.category,
        priority: newTicket.priority,
        status: 'new',
        userId: currentUser.uid
      });

      // Reset form
      setNewTicket({
        subject: '',
        description: '',
        category: '',
        priority: 'medium'
      });

      // Show success notification
      showNotification(toStr(t('supportclient.newTicket.success')), 'success');
      
      // Refresh tickets list and switch to tickets tab
      const userTickets = await getTickets({ userId: currentUser.uid });
      setTickets(userTickets);
      setFilteredTickets(userTickets);
      setTabValue(0);
    } catch (error) {
      console.error("Error creating ticket:", error);
      showNotification(toStr(t('supportclient.newTicket.error')), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'info';
      case 'inProgress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'medium':
        return 'info';
      case 'high':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Fonction pour afficher les détails d'un ticket
  const handleViewTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetails(true);
  };

  // Fonction pour fermer les détails d'un ticket
  const handleCloseTicketDetails = () => {
    setShowTicketDetails(false);
    setSelectedTicket(null);
  };

  // Fonction pour ajouter un message à un ticket
  const handleAddMessage = async () => {
    if (!replyMessage.trim() || !selectedTicket || !currentUser) return;
    
    setReplying(true);
    try {
      // Créer un nouveau message
      const message = {
        text: replyMessage,
        userId: currentUser.uid,
        isStaff: false,
        createdAt: new Date()
      };
      
      // Mettre à jour le ticket avec le nouveau message
      const updatedTicket = {
        ...selectedTicket,
        messages: selectedTicket.messages ? [...selectedTicket.messages, message] : [message],
        updatedAt: new Date()
      };
      
      await updateTicket(selectedTicket.id, updatedTicket);
      
      // Rafraîchir les tickets et sélectionner le ticket mis à jour
      const userTickets = await getTickets({ userId: currentUser.uid });
      setTickets(userTickets);
      setFilteredTickets(userTickets);
      
      // Trouver le ticket mis à jour dans la liste
      const updatedSelectedTicket = userTickets.find(ticket => ticket.id === selectedTicket.id);
      if (updatedSelectedTicket) {
        setSelectedTicket(updatedSelectedTicket);
      }
      
      // Réinitialiser le message de réponse
      setReplyMessage('');
      showNotification(toStr(t('supportclient.messages.sent')), 'success');
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      showNotification(toStr(t('supportclient.messages.error')), 'error');
    } finally {
      setReplying(false);
    }
  };

  // Fonction pour changer le statut d'un ticket
  const handleChangeTicketStatus = async () => {
    if (!selectedTicket || !currentUser) return;
    
    setLoading(true);
    try {
      const newStatus = selectedTicket.status === 'closed' ? 'new' : 'closed';
      
      // Mettre à jour le statut du ticket
      await updateTicket(selectedTicket.id, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Rafraîchir les tickets et sélectionner le ticket mis à jour
      const userTickets = await getTickets({ userId: currentUser.uid });
      setTickets(userTickets);
      setFilteredTickets(userTickets);
      
      // Trouver le ticket mis à jour dans la liste
      const updatedSelectedTicket = userTickets.find(ticket => ticket.id === selectedTicket.id);
      if (updatedSelectedTicket) {
        setSelectedTicket(updatedSelectedTicket);
      }
      
      const statusMessage = newStatus === 'closed' 
        ? toStr(t('supportclient.actions.ticketClosed'))
        : toStr(t('supportclient.actions.ticketReopened'));
      showNotification(statusMessage, 'success');
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      showNotification(toStr(t('supportclient.actions.statusChangeError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Ajout de statistiques pour les tickets
  const getTicketStats = () => {
    const stats = {
      active: tickets.filter(t => !['resolved', 'closed'].includes(t.status)).length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      pending: tickets.filter(t => t.status === 'pending').length,
      closed: tickets.filter(t => t.status === 'closed').length,
      total: tickets.length
    };
    return stats;
  };

  if (loading && tickets.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const ticketStats = getTicketStats();

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {/* Header avec fond dégradé */}
        <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
            component="h1" 
              sx={{
                fontWeight: 700,
                mb: 1,
              background: 'linear-gradient(90deg, #015480, #0288d1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <SupportIcon sx={{ fontSize: 32 }} />
            {toStr(t('supportclient.title'))}
            </Typography>
          <Typography variant="body1" color="text.secondary">
            {toStr(t('supportclient.subtitle'))}
            </Typography>
          </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Section principale */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(66.666% - 1.5rem)' } }}>
            {/* Cartes de statistiques */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1rem)', md: 'calc(20% - 1.6rem)' } }}>
                <Card sx={{ 
                  bgcolor: 'primary.light', 
                  color: 'white', 
                  height: '100%', 
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{ticketStats.active}</Typography>
                    <Typography variant="body2">{toStr(t('dashboard.client.activeTickets'))}</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1rem)', md: 'calc(20% - 1.6rem)' } }}>
                <Card sx={{ 
                  bgcolor: 'success.light', 
                  color: 'white', 
                  height: '100%', 
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{ticketStats.resolved}</Typography>
                    <Typography variant="body2">{toStr(t('dashboard.client.resolvedTickets'))}</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1rem)', md: 'calc(20% - 1.6rem)' } }}>
                <Card sx={{ 
                  bgcolor: 'warning.light', 
                  color: 'white', 
                  height: '100%', 
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{ticketStats.pending}</Typography>
                    <Typography variant="body2">{toStr(t('dashboard.client.pendingTickets'))}</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1rem)', md: 'calc(20% - 1.6rem)' } }}>
                <Card sx={{ 
                  bgcolor: 'error.light', 
                  color: 'white', 
                  height: '100%', 
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{ticketStats.closed}</Typography>
                    <Typography variant="body2">{toStr(t('supportclient.status.closed'))}</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1rem)', md: 'calc(20% - 1.6rem)' } }}>
                <Card sx={{ 
                  bgcolor: 'info.light', 
                  color: 'white', 
                  height: '100%', 
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{ticketStats.total}</Typography>
                    <Typography variant="body2">{toStr(t('supportclient.tickets.all'))}</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

        {/* Tabs Navigation */}
            <Paper 
              elevation={0} 
              sx={{ 
                borderRadius: 2,
                mb: 4,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="support tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 64
              }
            }}
          >
                <Tab icon={<SupportIcon />} iconPosition="start" label={toStr(t('supportclient.tabs.tickets'))} />
                <Tab icon={<AddIcon />} iconPosition="start" label={toStr(t('supportclient.tabs.newTicket'))} />
          </Tabs>

              {/* Contenu des onglets */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('supportclient.tickets.title')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="status-filter-label">{t('supportclient.tickets.status')}</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label={t('supportclient.tickets.status')}
                  >
                    <MenuItem value="all">{t('supportclient.tickets.all')}</MenuItem>
                    <MenuItem value="new">{t('supportclient.status.new')}</MenuItem>
                    <MenuItem value="inProgress">{t('supportclient.status.inProgress')}</MenuItem>
                    <MenuItem value="pending">{t('supportclient.status.pending')}</MenuItem>
                    <MenuItem value="resolved">{t('supportclient.status.resolved')}</MenuItem>
                    <MenuItem value="closed">{t('supportclient.status.closed')}</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setTabValue(1)}
                >
                  {t('supportclient.tickets.createNew')}
                </Button>
              </Box>
            </Box>
            
            {filteredTickets.length > 0 ? (
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table sx={{ minWidth: 650 }}>
                      <TableHead sx={{ bgcolor: 'primary.light' }}>
                    <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.id')}</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.subject')}</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.status')}</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.priority')}</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.created')}</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.lastUpdated')}</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t('supportclient.tickets.view')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': { bgcolor: 'action.hover' },
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontWeight="medium" color="primary">
                                {ticket.ticketId}
                              </Typography>
                            </TableCell>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                          {ticket.subject}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={t(`supportclient.status.${ticket.status}`)} 
                            color={getStatusColor(ticket.status) as any} 
                            size="small" 
                                sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={t(`supportclient.priorities.${ticket.priority}`)} 
                            color={getPriorityColor(ticket.priority) as any} 
                            size="small" 
                            variant="outlined"
                                sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(ticket.createdAt)}</TableCell>
                        <TableCell>{formatDate(ticket.updatedAt)}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewTicket(ticket);
                                }}
                          >
                            {t('supportclient.tickets.view')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.default' }}>
                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                  <SupportIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {t('supportclient.tickets.noTickets')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => setTabValue(1)}
                  >
                    {t('supportclient.tickets.createNew')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('supportclient.newTicket.title')}
            </Typography>
            
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('supportclient.newTicket.subject')}
                name="subject"
                value={newTicket.subject}
                    onChange={handleNewTicketInputChange}
                error={!!errors.subject}
                helperText={errors.subject}
                required
              />
              
              <TextField
                fullWidth
                label={t('supportclient.newTicket.description')}
                name="description"
                value={newTicket.description}
                    onChange={handleNewTicketInputChange}
                multiline
                rows={6}
                error={!!errors.description}
                helperText={errors.description}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <FormControl fullWidth error={!!errors.category} required>
                  <InputLabel>{t('supportclient.newTicket.category')}</InputLabel>
                  <Select
                    name="category"
                    value={newTicket.category}
                    label={t('supportclient.newTicket.category')}
                        onChange={handleNewTicketSelectChange}
                  >
                    <MenuItem value="technical">{t('supportclient.categories.technical')}</MenuItem>
                    <MenuItem value="billing">{t('supportclient.categories.billing')}</MenuItem>
                    <MenuItem value="account">{t('supportclient.categories.account')}</MenuItem>
                    <MenuItem value="feature">{t('supportclient.categories.feature')}</MenuItem>
                    <MenuItem value="other">{t('supportclient.categories.other')}</MenuItem>
                  </Select>
                  {errors.category && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>{t('supportclient.newTicket.priority')}</InputLabel>
                  <Select
                    name="priority"
                    value={newTicket.priority}
                    label={t('supportclient.newTicket.priority')}
                        onChange={handleNewTicketSelectChange}
                  >
                    <MenuItem value="low">{t('supportclient.priorities.low')}</MenuItem>
                    <MenuItem value="medium">{t('supportclient.priorities.medium')}</MenuItem>
                    <MenuItem value="high">{t('supportclient.priorities.high')}</MenuItem>
                    <MenuItem value="critical">{t('supportclient.priorities.critical')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  sx={{ borderStyle: 'dashed' }}
                >
                  {t('supportclient.newTicket.addAttachment')}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  (Optional)
                </Typography>
              </Box>
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setTabValue(0)}
              >
                {t('supportclient.newTicket.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleSubmitNewTicket}
                disabled={loading}
              >
                {loading ? toStr(t('common.saving')) : toStr(t('supportclient.newTicket.submit'))}
              </Button>
            </Box>
          </TabPanel>
        </Paper>
          </Box>

          {/* Sidebar d'aide et ressources */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(33.333% - 1.5rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 0, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                overflow: 'hidden'
              }}
            >
              {/* En-tête de la section */}
              <Box 
                sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', 
                  color: 'white',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#ffffff',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  <HelpOutlineIcon 
                    fontSize="large" 
                    sx={{ 
                      color: '#ffffff',
                      filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                    }} 
                  />
                  {toStr(t('supportclient.help.title'))}
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mt: 1, 
                    opacity: 0.9,
                    color: '#ffffff',
                    textShadow: '0px 1px 1px rgba(0,0,0,0.2)'
                  }}
                >
                  {toStr(t('supportclient.help.description'))}
                </Typography>
              </Box>
              
              {/* Liste des ressources */}
              <Box sx={{ p: 2 }}>
                <List>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItem>
                      <ListItemIcon>
                        <ArticleIcon color="primary" fontSize="large" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" fontWeight={600}>
                            {toStr(t('dashboard.client.knowledgeBase'))}
                          </Typography>
                        }
                        secondary={toStr(t('dashboard.client.knowledgeBaseDesc'))}
                      />
                    </ListItem>
                  </Paper>

                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItem>
                      <ListItemIcon>
                        <LiveHelpIcon color="primary" fontSize="large" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" fontWeight={600}>
                            {toStr(t('dashboard.client.userGuides'))}
                          </Typography>
                        }
                        secondary={toStr(t('dashboard.client.userGuidesDesc'))}
                      />
                    </ListItem>
                  </Paper>

                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItem>
                      <ListItemIcon>
                        <ChatIcon color="primary" fontSize="large" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" fontWeight={600}>
                            {toStr(t('dashboard.client.contactSupport'))}
                          </Typography>
                        }
                        secondary={toStr(t('dashboard.client.contactSupportDesc'))}
                      />
                    </ListItem>
                  </Paper>
                </List>
              </Box>

              <Divider />

              {/* Section de contact rapide */}
              <Box sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography 
                  variant="subtitle1" 
                  color="primary" 
                  fontWeight={600} 
                  sx={{ 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <SupportAgentIcon />
                  {toStr(t('dashboard.client.needHelp'))}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2 
                }}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <EmailIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        support@example.com
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Réponse sous 24h
                      </Typography>
                    </Box>
                  </Paper>
                  
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <PhoneIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        +33 1 23 45 67 89
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lun-Ven, 9h-18h
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth 
                  startIcon={<ChatIcon />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  Démarrer un chat en direct
                </Button>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  {toStr(t('dashboard.client.supportHours'))}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Ticket Details Dialog */}
        <Dialog 
          open={showTicketDetails} 
          onClose={handleCloseTicketDetails}
          fullWidth
          maxWidth="md"
        >
          {selectedTicket && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <Box>
                  <Typography variant="h6" component="div">
                    {selectedTicket.subject}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {selectedTicket.ticketId}
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseTicketDetails} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%' }}>
                  <Box sx={{ flex: '1 1 auto', width: { xs: '100%', md: '66.66%' }, p: 3, borderRight: { xs: 'none', md: '1px solid rgba(0, 0, 0, 0.12)' } }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {toStr(t('supportclient.ticketDetails.description'))}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                      <Typography variant="body1">
                        {selectedTicket.description}
                      </Typography>
                    </Paper>

                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {toStr(t('supportclient.ticketDetails.conversation'))}
                    </Typography>
                    {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                      <Stack spacing={2}>
                        {selectedTicket.messages.map((message) => (
                          <Paper 
                            key={message.id} 
                            sx={{ 
                              p: 2, 
                              bgcolor: message.isStaff ? 'primary.light' : 'background.default',
                              borderLeft: message.isStaff ? '4px solid' : 'none',
                              borderColor: message.isStaff ? 'primary.dark' : 'none',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                          >
                            <Typography variant="body2" color={message.isStaff ? 'white' : 'text.primary'}>
                              {message.text}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="caption" color={message.isStaff ? 'white' : 'text.secondary'}>
                                {message.isStaff ? toStr(t('supportclient.messages.support')) : toStr(t('supportclient.messages.you'))}
                              </Typography>
                              <Typography variant="caption" color={message.isStaff ? 'white' : 'text.secondary'}>
                                {formatDate(message.createdAt)}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.noMessages'))}
                        </Typography>
                      </Paper>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <TextField
                        fullWidth
                        placeholder={toStr(t('supportclient.ticketDetails.replyPlaceholder'))}
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        disabled={replying}
                      />
                      <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<SendIcon />}
                        onClick={handleAddMessage}
                        disabled={replying || !replyMessage.trim()}
                      >
                        {replying ? toStr(t('supportclient.messages.sending')) : toStr(t('supportclient.ticketDetails.reply'))}
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 3, bgcolor: 'background.default' }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                      {toStr(t('supportclient.ticketDetails.status'))}
                    </Typography>
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.status'))}
                        </Typography>
                        <Chip 
                          label={toStr(t(`supportclient.status.${selectedTicket.status}`))} 
                          color={getStatusColor(selectedTicket.status) as any} 
                          size="small" 
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.priority'))}
                        </Typography>
                        <Chip 
                          label={toStr(t(`supportclient.priorities.${selectedTicket.priority}`))} 
                          color={getPriorityColor(selectedTicket.priority) as any} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.category'))}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {toStr(t(`supportclient.categories.${selectedTicket.category}`))}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.created'))}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedTicket.createdAt)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          {toStr(t('supportclient.ticketDetails.updated'))}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedTicket.updatedAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
                <Button onClick={handleCloseTicketDetails} variant="outlined">
                  {toStr(t('supportclient.ticketDetails.close'))}
                </Button>
                <Button 
                  variant="contained" 
                  color={selectedTicket.status === 'closed' ? 'primary' : 'error'} 
                  disabled={loading}
                  onClick={handleChangeTicketStatus}
                  startIcon={selectedTicket.status === 'closed' ? <AddIcon /> : <CloseIcon fontSize="small" />}
                >
                  {selectedTicket.status === 'closed' ? toStr(t('supportclient.ticketDetails.reopen')) : toStr(t('supportclient.ticketDetails.closeTicket'))}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
        <LiveChat />
      </Container>
    </Box>
  );
};

export default SupportClient;